// src/components/PaletteSwitcher.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Floating palette picker — one click to switch all chart colors app-wide.
// Shows preset swatches, a custom-color editor, and a live preview strip.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useChartPalette, PALETTE_PRESETS } from '../context/ChartPaletteContext';

const SWATCH_SIZE = 12;

// ── Tiny swatch row ─────────────────────────────────────────────────────────
const SwatchRow = ({ colors, size = SWATCH_SIZE }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {colors.slice(0, 10).map((c, i) => (
      <div key={i} style={{ width: size, height: size, borderRadius: 2, background: c, flexShrink: 0 }} />
    ))}
  </div>
);

// ── Custom-color editor row ─────────────────────────────────────────────────
function CustomEditor({ onApply }) {
  const { colors: currentColors, paletteId } = useChartPalette();
  const [slots, setSlots] = useState(() =>
    paletteId === 'custom' ? [...currentColors] : [...PALETTE_PRESETS[0].colors]
  );
  const [editIdx, setEditIdx] = useState(null);
  const [hexInput, setHexInput] = useState('');

  const updateSlot = (i, color) => {
    const next = [...slots];
    next[i] = color;
    setSlots(next);
  };

  return (
    <div style={{ padding: '10px 0 4px' }}>
      <div style={{ fontSize: 9, color: '#8396b3', letterSpacing: '.1em', marginBottom: 8, fontWeight: 600 }}>
        CUSTOM COLORS
      </div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
        {slots.slice(0, 10).map((c, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <div
              onClick={() => { setEditIdx(editIdx === i ? null : i); setHexInput(c); }}
              style={{
                width: 28, height: 28, borderRadius: 4, background: c, cursor: 'pointer',
                border: editIdx === i ? '2px solid #fff' : '2px solid transparent',
                transition: 'border .15s',
              }}
            />
            <div style={{ fontSize: 7, color: '#627795', textAlign: 'center', marginTop: 2, fontFamily: 'JetBrains Mono,monospace' }}>
              {i + 1}
            </div>
          </div>
        ))}
      </div>
      {editIdx !== null && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
          <input
            type="color"
            value={slots[editIdx]}
            onChange={(e) => { updateSlot(editIdx, e.target.value); setHexInput(e.target.value); }}
            style={{ width: 30, height: 26, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
          />
          <input
            type="text"
            value={hexInput}
            onChange={(e) => {
              const v = e.target.value;
              setHexInput(v);
              if (/^#[0-9a-fA-F]{6}$/.test(v)) updateSlot(editIdx, v);
            }}
            maxLength={7}
            style={{
              flex: 1, height: 26, background: '#0a1120', border: '1px solid #1e293b',
              borderRadius: 3, color: '#e2e8f0', fontSize: 11, fontFamily: 'JetBrains Mono,monospace',
              padding: '0 8px', outline: 'none',
            }}
            placeholder="#hex"
          />
          <span style={{ fontSize: 8, color: '#627795' }}>Slot {editIdx + 1}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <div style={{ flex: 1, fontSize: 8, color: '#627795' }}>
          Paste comma-separated hex:
        </div>
      </div>
      <input
        type="text"
        placeholder="#22d3ee, #4ade80, #a78bfa, ..."
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const parsed = e.target.value.split(',').map(s => s.trim()).filter(s => /^#[0-9a-fA-F]{3,6}$/.test(s));
            if (parsed.length >= 2) {
              const expanded = parsed.map(c => c.length === 4 ? '#' + c[1]+c[1]+c[2]+c[2]+c[3]+c[3] : c);
              setSlots(expanded.concat(slots.slice(expanded.length)).slice(0, 10));
            }
          }
        }}
        style={{
          width: '100%', height: 26, background: '#0a1120', border: '1px solid #1e293b',
          borderRadius: 3, color: '#e2e8f0', fontSize: 10, fontFamily: 'JetBrains Mono,monospace',
          padding: '0 8px', outline: 'none', marginTop: 4, marginBottom: 8,
        }}
      />
      <button
        onClick={() => onApply(slots)}
        style={{
          width: '100%', height: 30, background: 'linear-gradient(135deg, #22d3ee 0%, #a78bfa 100%)',
          border: 'none', borderRadius: 4, color: '#020508', fontSize: 10, fontWeight: 700,
          fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.08em', cursor: 'pointer',
          transition: 'opacity .15s',
        }}
        onMouseEnter={(e) => e.target.style.opacity = '0.85'}
        onMouseLeave={(e) => e.target.style.opacity = '1'}
      >
        APPLY CUSTOM PALETTE
      </button>
    </div>
  );
}

// ── Main Switcher ───────────────────────────────────────────────────────────
export default function PaletteSwitcher() {
  const { paletteId, colors, setPalette, setCustomPalette } = useChartPalette();
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const ref = useRef(null);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePreset = useCallback((id) => {
    setPalette(id);
    // don't close - let them see the change, they can click away to dismiss
  }, [setPalette]);

  const handleCustomApply = useCallback((c) => {
    setCustomPalette(c);
    setShowCustom(false);
  }, [setCustomPalette]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        title="Chart color palette"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          height: 30, padding: '0 10px',
          background: open ? '#0f182640' : 'transparent',
          border: '1px solid #1e293b', borderRadius: 4,
          cursor: 'pointer', transition: 'all .15s',
          color: '#b6c3d8',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#22d3ee40'; e.currentTarget.style.background = '#0f182640'; }}
        onMouseLeave={(e) => { if (!open) { e.currentTarget.style.borderColor = '#1e293b'; e.currentTarget.style.background = 'transparent'; }}}
      >
        {/* Mini swatch preview */}
        <div style={{ display: 'flex', gap: 2 }}>
          {colors.slice(0, 5).map((c, i) => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
          ))}
        </div>
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.5 }}>
          <path d="M14.5 1.5l-1.3 1.3M10.8 5.2l-6.6 6.6L2 14l2.2-2.2 6.6-6.6m2-2L13.1 1 15 2.9l-2.2 2.3m-2 0l2-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6,
          width: 290, maxHeight: 460, overflowY: 'auto',
          background: '#0a1120', border: '1px solid #1e293b',
          borderRadius: 6, boxShadow: '0 12px 40px rgba(0,0,0,.6)',
          zIndex: 999, padding: '12px 14px',
          animation: 'paletteSlideIn .18s cubic-bezier(.16,1,.3,1)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0', fontFamily: 'Syne,sans-serif' }}>
                Chart Palette
              </div>
              <div style={{ fontSize: 8, color: '#627795', marginTop: 1 }}>
                Changes all charts & reports instantly
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: '#627795', cursor: 'pointer', fontSize: 14, padding: '2px 4px' }}
            >
              ✕
            </button>
          </div>

          {/* Active preview */}
          <div style={{
            padding: '8px 10px', background: '#060b14', border: '1px solid #1e293b',
            borderRadius: 4, marginBottom: 12,
          }}>
            <div style={{ fontSize: 8, color: '#627795', letterSpacing: '.1em', marginBottom: 6 }}>
              ACTIVE: {paletteId === 'custom' ? 'CUSTOM' : PALETTE_PRESETS.find(p => p.id === paletteId)?.name.toUpperCase()}
            </div>
            <SwatchRow colors={colors} size={18} />
          </div>

          {/* Preset list */}
          {PALETTE_PRESETS.map((preset) => {
            const active = preset.id === paletteId;
            return (
              <div
                key={preset.id}
                onClick={() => handlePreset(preset.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', marginBottom: 3, borderRadius: 4,
                  background: active ? '#22d3ee0d' : 'transparent',
                  border: active ? '1px solid #22d3ee30' : '1px solid transparent',
                  cursor: 'pointer', transition: 'all .12s',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#0f182680'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <SwatchRow colors={preset.colors} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: active ? '#22d3ee' : '#e2e8f0' }}>
                    {preset.name}
                  </div>
                  <div style={{ fontSize: 8, color: '#627795', marginTop: 1 }}>
                    {preset.description}
                  </div>
                </div>
                {active && (
                  <div style={{ fontSize: 8, color: '#22d3ee', fontWeight: 700, letterSpacing: '.1em', flexShrink: 0 }}>
                    ✓
                  </div>
                )}
              </div>
            );
          })}

          {/* Custom section toggle */}
          <div style={{ borderTop: '1px solid #1e293b', margin: '8px 0', padding: 0 }} />
          <div
            onClick={() => setShowCustom(!showCustom)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
              background: paletteId === 'custom' ? '#a78bfa0d' : 'transparent',
              border: paletteId === 'custom' ? '1px solid #a78bfa30' : '1px solid transparent',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#0f182680'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = paletteId === 'custom' ? '#a78bfa0d' : 'transparent'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12 }}>🎨</span>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: paletteId === 'custom' ? '#a78bfa' : '#e2e8f0' }}>
                  Custom Brand Colors
                </div>
                <div style={{ fontSize: 8, color: '#627795', marginTop: 1 }}>
                  Pick individual colors or paste hex codes
                </div>
              </div>
            </div>
            <span style={{ fontSize: 10, color: '#627795', transition: 'transform .15s', transform: showCustom ? 'rotate(180deg)' : 'none' }}>
              ▾
            </span>
          </div>
          {showCustom && <CustomEditor onApply={handleCustomApply} />}
        </div>
      )}

      {/* Animation keyframe */}
      <style>{`
        @keyframes paletteSlideIn {
          from { opacity: 0; transform: translateY(-4px) scale(.98); }
          to   { opacity: 1; transform: translateY(0)  scale(1); }
        }
      `}</style>
    </div>
  );
}
